import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  deliverHostedMail,
  insertFamilyMail,
  loadMailMessages,
  loadNotificationSettings,
  onRemoteSession,
  queueStaffMail,
  remoteClient,
  saveNotificationSettings,
  subscribeChanges,
} from "../data/remote";
import { setRegistrationNoticeListener } from "../domain/email/notices";
import {
  deliverEmail,
  guardStoredMail,
  planDelivery,
  unconfiguredEmailProvider,
} from "../domain/email/service";
import { runtimeFromBrowser } from "../domain/env";
import {
  emptyMailState,
  latestEventMail,
  latestFamilyMail,
  DEFAULT_STAFF_INBOX,
  MAIL_STORAGE_KEY,
  normalizeSettings,
  sendEventRegistrationMail,
  sendRegistrationMail,
  type RegistrationMailReason,
  staffInbox,
  staffRsvpCopy,
  type MailMessage,
  type MailState,
  type NotificationSettings,
} from "../domain/mail";
import type { Student } from "../domain/data";
import { useLocale } from "./i18n";
import { useStudents } from "./students";

type MailContextValue = {
  settings: NotificationSettings;
  messages: MailMessage[];
  staffMessages: MailMessage[];
  saveSettings: (settings: NotificationSettings) => void;
  notifyRegistration: (
    student: Student,
    reason?: RegistrationMailReason,
  ) => {
    family: MailMessage;
    staff: MailMessage | null;
  };
  familyMailFor: (studentId: string) => MailMessage | undefined;
  eventMailFor: (studentId: string, eventId: string) => MailMessage | undefined;
};

const MailContext = createContext<MailContextValue | null>(null);

function persist(state: MailState) {
  try {
    localStorage.setItem(MAIL_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore quota / private mode */
  }
}

function readStore(): MailState {
  const seeded = emptyMailState();
  if (typeof window === "undefined") return seeded;
  try {
    const raw = localStorage.getItem(MAIL_STORAGE_KEY);
    if (!raw) return seeded;
    const parsed = JSON.parse(raw) as Partial<MailState>;
    return {
      settings: normalizeSettings(parsed.settings),
      messages: Array.isArray(parsed.messages) ? parsed.messages : [],
    };
  } catch {
    return seeded;
  }
}

function deliveryLabel(
  action: "send" | "redirect" | "block",
  delivered: boolean,
): "recorded" | "blocked" | "redirected" | "sent" {
  if (delivered) return "sent";
  if (action === "block") return "blocked";
  if (action === "redirect") return "redirected";
  return "recorded";
}

export function MailProvider({ children }: { children: ReactNode }) {
  const { locale } = useLocale();
  const { students } = useStudents();
  const studentsRef = useRef(students);
  studentsRef.current = students;
  const localeRef = useRef(locale);
  localeRef.current = locale;
  const [store, setStore] = useState<MailState>(() =>
    remoteClient() ? emptyMailState() : readStore(),
  );
  const storeRef = useRef(store);
  storeRef.current = store;
  const loadGen = useRef(0);

  const write = useCallback((next: MailState) => {
    storeRef.current = next;
    setStore(next);
    if (!remoteClient()) persist(next);
  }, []);

  const reload = useCallback(async () => {
    const client = remoteClient();
    if (!client) return;
    const token = ++loadGen.current;
    try {
      const [messages, settings] = await Promise.all([
        loadMailMessages(client),
        loadNotificationSettings(client),
      ]);
      if (token !== loadGen.current) return;
      const next = {
        settings: settings ? normalizeSettings(settings) : storeRef.current.settings,
        messages,
      };
      storeRef.current = next;
      setStore(next);
    } catch {
      /* keep the last snapshot */
    }
  }, []);

  useEffect(() => {
    const client = remoteClient();
    if (!client) return;
    const stopSession = onRemoteSession(client, () => {
      void reload();
    });
    const stop = subscribeChanges(client, ["mail_messages", "app_settings"], () => {
      void reload();
    });
    const onFocus = () => {
      if (document.visibilityState === "visible") void reload();
    };
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      stopSession();
      stop();
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [reload]);

  const saveSettings = useCallback(
    (settings: NotificationSettings) => {
      const next = normalizeSettings(settings);
      write({ ...storeRef.current, settings: next });
      const client = remoteClient();
      if (client) void saveNotificationSettings(client, next).catch(() => undefined);
    },
    [write],
  );

  const recordFamily = useCallback(
    async (message: MailMessage, eventId: string | null): Promise<string | null> => {
      const runtime = runtimeFromBrowser();
      const redirect = import.meta.env.VITE_QA_EMAIL_REDIRECT;
      const guarded = guardStoredMail(message, runtime, redirect);
      const plan = planDelivery(message.to, runtime, redirect);
      try {
        const result = await deliverEmail(
          { to: message.to, subject: guarded.subject, text: message.body },
          runtime,
          redirect,
          unconfiguredEmailProvider,
        );
        write({
          ...storeRef.current,
          messages: [
            guarded,
            ...storeRef.current.messages.filter((item) => item.id !== guarded.id),
          ],
        });
        const client = remoteClient();
        if (!client) return null;
        const id = await insertFamilyMail(
          client,
          { ...message, subject: guarded.subject },
          eventId,
          deliveryLabel(plan.action, result.delivered),
        );
        if (id && plan.action !== "block") {
          await deliverHostedMail(client, id);
        }
        return id;
      } catch {
        /* keep the in-app copy; do not throw from a mail hook */
        return null;
      }
    },
    [write],
  );

  const notifyRegistration = useCallback(
    (student: Student, reason: RegistrationMailReason = "registered") => {
      const result = sendRegistrationMail(storeRef.current, student, locale, undefined, reason);
      const runtime = runtimeFromBrowser();
      const redirect = import.meta.env.VITE_QA_EMAIL_REDIRECT;
      const family = guardStoredMail(result.family, runtime, redirect);
      const staff = result.staff
        ? guardStoredMail(result.staff, runtime, redirect)
        : null;
      write({
        ...result.state,
        messages: result.state.messages.map((message) => {
          if (message.id === family.id) return family;
          if (staff && message.id === staff.id) return staff;
          return message;
        }),
      });
      void recordFamily(result.family, null);
      const client = remoteClient();
      if (client && staff) {
        void queueStaffMail(
          client,
          "staff_registration",
          student.id,
          null,
          staff.subject,
          staff.body,
        )
          .then((queued) => {
            if (!queued.id || queued.to.length === 0) return;
            if (planDelivery(queued.to, runtime, redirect).action === "block") return;
            return deliverHostedMail(client, queued.id);
          })
          .catch(() => undefined);
      } else if (staff) {
        void deliverEmail(
          { to: staff.to, subject: staff.subject, text: staff.body },
          runtime,
          redirect,
          unconfiguredEmailProvider,
        );
      }
      return { family, staff };
    },
    [locale, recordFamily, write],
  );

  useEffect(() => {
    setRegistrationNoticeListener((notice) => {
      const student = studentsRef.current.find((item) => item.id === notice.studentId);
      if (!student) return;
      const drafted = sendEventRegistrationMail(
        storeRef.current,
        student,
        {
          id: notice.eventId,
          name: notice.eventName,
          dateLabel: notice.dateLabel,
          timeLabel: notice.timeLabel,
          location: notice.location,
        },
        notice.status,
        localeRef.current,
        new Date().toISOString(),
        Boolean(notice.promoted),
      );
      if (!drafted.family) return;

      const runtime = runtimeFromBrowser();
      const redirect = import.meta.env.VITE_QA_EMAIL_REDIRECT;
      const client = remoteClient();
      if (!client) {
        const family = guardStoredMail(drafted.family, runtime, redirect);
        const staff = drafted.staff
          ? guardStoredMail(drafted.staff, runtime, redirect)
          : null;
        write({
          ...drafted.state,
          messages: drafted.state.messages.map((message) => {
            if (message.id === family.id) return family;
            if (staff && message.id === staff.id) return staff;
            return message;
          }),
        });
        void recordFamily(drafted.family, notice.eventId);
        if (staff) {
          void deliverEmail(
            { to: staff.to, subject: staff.subject, text: staff.body },
            runtime,
            redirect,
            unconfiguredEmailProvider,
          );
        }
        return;
      }

      void recordFamily(drafted.family, notice.eventId);
      const staffCopy = drafted.staff
        ? { subject: drafted.staff.subject, body: drafted.staff.body }
        : staffRsvpCopy(
            student,
            notice.eventName,
            notice.status,
            Boolean(notice.promoted),
          );
      const guarded = guardStoredMail(
        {
          ...(drafted.staff ?? drafted.family),
          kind: "staff_rsvp",
          to: drafted.staff?.to ?? [],
          from: DEFAULT_STAFF_INBOX,
          subject: staffCopy.subject,
          body: staffCopy.body,
        },
        runtime,
        redirect,
      );
      void queueStaffMail(
        client,
        "staff_rsvp",
        student.id,
        notice.eventId,
        guarded.subject,
        staffCopy.body,
      )
        .then((queued) => {
          if (queued.to.length === 0) return;
          const staff: MailMessage = {
            ...guarded,
            id: queued.id ?? crypto.randomUUID(),
            kind: "staff_rsvp",
            to: queued.to,
            eventId: notice.eventId,
          };
          write({
            ...storeRef.current,
            messages: [guardStoredMail(staff, runtime, redirect), ...storeRef.current.messages],
          });
          if (!queued.id || planDelivery(queued.to, runtime, redirect).action === "block") return;
          return deliverHostedMail(client, queued.id);
        })
        .catch(() => undefined);
    });
    return () => setRegistrationNoticeListener(null);
  }, [recordFamily, write]);

  const value = useMemo<MailContextValue>(
    () => ({
      settings: store.settings,
      messages: store.messages,
      staffMessages: staffInbox(store.messages),
      saveSettings,
      notifyRegistration,
      familyMailFor: (studentId) => latestFamilyMail(store.messages, studentId),
      eventMailFor: (studentId, eventId) =>
        latestEventMail(store.messages, studentId, eventId),
    }),
    [store, saveSettings, notifyRegistration],
  );

  return <MailContext.Provider value={value}>{children}</MailContext.Provider>;
}

export function useMail(): MailContextValue {
  const ctx = useContext(MailContext);
  if (!ctx) throw new Error("useMail must be used within MailProvider");
  return ctx;
}
